import { Trans, useLingui } from '@lingui/react/macro'
import { usePermissions } from '$src/lib/hooks/usePermissions'
import { customerHooks } from '$src/lib/trpcHooks'
import { EditCustomerSettingsForm } from '$src/components/profile/CustomerCard'
import { UserEmailBadge } from '$src/components/company-portal/UserEmailBadge'
import { Center, Loader, Stack, Text } from '@mantine/core'
import { AppPage } from '@markku/components'
import { createFileRoute } from '@tanstack/react-router'

function CompanySettings() {
  const { t } = useLingui()
  const { companyId } = Route.useParams()
  const { isOwnerOf } = usePermissions()

  const { data: company, isLoading } = customerHooks.useGetById(
    Number(companyId),
  )

  if (isLoading) {
    return (
      <AppPage
        title={t`Settings`}
        backTarget={`/company-portal/${companyId}`}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </AppPage>
    )
  }

  if (!company) {
    return (
      <AppPage
        title={t`Settings`}
        backTarget={`/company-portal/${companyId}`}
        rightSection={<UserEmailBadge />}
      >
        <Center py="xl">
          <Text c="dimmed">
            <Trans>Company not found</Trans>
          </Text>
        </Center>
      </AppPage>
    )
  }

  const isOwner = isOwnerOf(company.id)

  return (
    <AppPage
      title={t`Settings`}
      backTarget={`/company-portal/${companyId}`}
      rightSection={<UserEmailBadge />}
    >
      {isOwner ? (
        <EditCustomerSettingsForm customer={company} />
      ) : (
        <ReadOnlySettings customer={company} />
      )}
    </AppPage>
  )
}

function ReadOnlySettings({
  customer,
}: {
  customer: {
    name: string
    billingEmail?: string | null
    members?: Array<{
      accessRole: 'owner' | 'member' | null
      user?: { email?: string | null } | null
    }> | null
  }
}) {
  const ownerEmail = customer.members?.find((m) => m.accessRole === 'owner')
    ?.user?.email
  const effectiveEmail = customer.billingEmail ?? ownerEmail

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        <Trans>Customer name</Trans>
      </Text>
      <Text fw={500}>{customer.name}</Text>
      <Text size="sm" c="dimmed">
        <Trans>Invoices are sent to</Trans>
      </Text>
      <Text fw={500}>{effectiveEmail || '–'}</Text>
    </Stack>
  )
}

export const Route = createFileRoute(
  '/_app/company-portal/$companyId/settings',
)({
  component: CompanySettings,
})
